import Decimal from 'decimal.js';

/**
 * 高精度数学计算工具（基于 decimal.js）
 * @namespace
 */
export class PrecisionUtils {
  /**
   * 验证输入值是否有效
   * @private
   * @param {*} value - 要验证的值
   * @param {string} paramName - 参数名称（用于错误信息）
   * @returns {boolean}
   */
  static _isValidNumber(value) {
    if (value === undefined || value === null || value === '') {
      return false;
    }

    // 转换为数字验证
    const num = Number(value);
    return !isNaN(num) && isFinite(num);
  }

  /**
   * 安全转换数值，提供默认值
   * @private
   * @param {*} value - 要转换的值
   * @param {number|string} defaultValue - 默认值
   * @returns {Decimal}
   */
  static _safeDecimal(value, defaultValue = 0) {
    if (!this._isValidNumber(value)) {
      if (!this._isValidNumber(defaultValue)) {
        throw new Error(`无效的数值: ${value} 和默认值: ${defaultValue}`);
      }
      return new Decimal(defaultValue);
    }
    return new Decimal(value);
  }

  /**
   * 安全加法 (a + b)
   * @param {number|string|Decimal} a - 第一个操作数
   * @param {number|string|Decimal} b - 第二个操作数
   * @param {number} [decimals=2] - 保留小数位数
   * @param {number|string} [defaultValue=0] - 无效值时的默认值
   * @returns {string}
   * @example
   * PrecisionUtils.add(0.1, 0.2) // "0.3"
   * PrecisionUtils.add(null, 10, 2, 0) // "10.00"
   */
  static add(a, b, decimals = 2, defaultValue = 0) {
    try {
      const decimalA = this._safeDecimal(a, defaultValue);
      const decimalB = this._safeDecimal(b, defaultValue);
      const result = decimalA.plus(decimalB);
      return this._formatResult(result, decimals);
    } catch (error) {
      console.error('加法计算错误:', error);
      return this._formatResult(new Decimal(defaultValue), decimals);
    }
  }

  /**
   * 安全减法 (a - b)
   * @param {number|string|Decimal} a - 被减数
   * @param {number|string|Decimal} b - 减数
   * @param {number} [decimals=2] - 保留小数位数
   * @param {number|string} [defaultValue=0] - 无效值时的默认值
   * @returns {string}
   * @example
   * PrecisionUtils.sub(0.3, 0.1) // "0.2"
   * PrecisionUtils.sub(undefined, 5, 2, 0) // "-5.00"
   */
  static sub(a, b, decimals = 2, defaultValue = 0) {
    try {
      const decimalA = this._safeDecimal(a, defaultValue);
      const decimalB = this._safeDecimal(b, defaultValue);
      const result = decimalA.minus(decimalB);
      return this._formatResult(result, decimals);
    } catch (error) {
      console.error('减法计算错误:', error);
      return this._formatResult(new Decimal(defaultValue), decimals);
    }
  }

  /**
   * 安全乘法 (a × b)
   * @param {number|string|Decimal} a - 被乘数
   * @param {number|string|Decimal} b - 乘数
   * @param {number} [decimals=2] - 保留小数位数
   * @param {number|string} [defaultValue=0] - 无效值时的默认值
   * @returns {string}
   * @example
   * PrecisionUtils.mul(0.1, 0.2) // "0.02"
   * PrecisionUtils.mul(null, 10, 2, 1) // "0.00"
   */
  static mul(a, b, decimals = 2, defaultValue = 0) {
    try {
      const decimalA = this._safeDecimal(a, defaultValue);
      const decimalB = this._safeDecimal(b, defaultValue);
      const result = decimalA.times(decimalB);

      return this._formatResult(result, decimals);
    } catch (error) {
      console.error('乘法计算错误:', error);
      return this._formatResult(new Decimal(defaultValue), decimals);
    }
  }

  /**
   * 安全除法 (a ÷ b)
   * @param {number|string|Decimal} a - 被除数
   * @param {number|string|Decimal} b - 除数
   * @param {number} [decimals=2] - 保留小数位数
   * @param {number|string} [defaultValue=0] - 无效值或除零时的默认值
   * @returns {string}
   * @example
   * PrecisionUtils.div(1, 3, 4) // "0.3333"
   * PrecisionUtils.div(10, null, 2, 0) // "0.00"
   * PrecisionUtils.div(10, 0, 2, 'N/A') // "N/A"
   */
  static div(a, b, decimals = 2, defaultValue = 0) {
    try {
      const decimalA = this._safeDecimal(a, defaultValue);
      const decimalB = this._safeDecimal(b, defaultValue);

      if (decimalB.isZero()) {
        throw new Error('Division by zero');
      }

      const result = decimalA.dividedBy(decimalB);
      return this._formatResult(result, decimals);
    } catch (error) {
      console.error('除法计算错误:', error);
      // 如果默认值也是数字，则格式化返回，否则直接返回默认值字符串
      if (this._isValidNumber(defaultValue)) {
        return this._formatResult(new Decimal(defaultValue), decimals);
      } else {
        return String(defaultValue);
      }
    }
  }

  /**
   * 金额分转元 (分 ÷ 100)
   * @param {number|string|Decimal} fen - 分单位金额
   * @param {number} [decimals=2] - 保留小数位数
   * @param {number|string} [defaultValue=0] - 无效值时的默认值
   * @returns {string}
   * @example
   * PrecisionUtils.fenToYuan(100) // "1.00"
   * PrecisionUtils.fenToYuan(null, 2, 0) // "0.00"
   */
  static fenToYuan(fen, decimals = 2, defaultValue = 0) {
    return this.div(fen, 100, decimals, defaultValue);
  }

  /**
   * 金额元转分 (元 × 100)
   * @param {number|string|Decimal} yuan - 元单位金额
   * @param {number|string} [defaultValue=0] - 无效值时的默认值
   * @returns {number}
   * @example
   * PrecisionUtils.yuanToFen(1.23) // 123
   * PrecisionUtils.yuanToFen(undefined, 0) // 0
   */
  static yuanToFen(yuan, defaultValue = 0) {
    try {
      const decimalYuan = this._safeDecimal(yuan, defaultValue);
      return decimalYuan.times(100).toNumber();
    } catch (error) {
      console.error('元转分计算错误:', error);
      return Number(defaultValue) || 0;
    }
  }

  /**
   * 比较两个数的大小
   * @param {number|string|Decimal} a - 第一个数
   * @param {number|string|Decimal} b - 第二个数
   * @param {number|string} [defaultA=0] - a无效时的默认值
   * @param {number|string} [defaultB=0] - b无效时的默认值
   * @returns {number} 1: a > b, 0: a = b, -1: a < b
   * @example
   * PrecisionUtils.compare(0.3, 0.1 + 0.2) // 0 (相等)
   * PrecisionUtils.compare(null, 5, 0, 0) // -1
   */
  static compare(a, b, defaultA = 0, defaultB = 0) {
    try {
      const decimalA = this._safeDecimal(a, defaultA);
      const decimalB = this._safeDecimal(b, defaultB);
      return decimalA.comparedTo(decimalB);
    } catch (error) {
      console.error('数值比较错误:', error);
      return 0; // 出错时返回相等
    }
  }

  /**
   * 检查数值是否有效
   * @param {*} value - 要检查的值
   * @returns {boolean}
   * @example
   * PrecisionUtils.isValidNumber(123) // true
   * PrecisionUtils.isValidNumber('abc') // false
   * PrecisionUtils.isValidNumber(null) // false
   */
  static isValidNumber(value) {
    return this._isValidNumber(value);
  }

  /**
   * 安全转换为数字
   * @param {*} value - 要转换的值
   * @param {number} [defaultValue=0] - 默认值
   * @returns {number}
   * @example
   * PrecisionUtils.toNumber('123.45') // 123.45
   * PrecisionUtils.toNumber(null, 100) // 100
   */
  static toNumber(value, defaultValue = 0) {
    try {
      const decimalValue = this._safeDecimal(value, defaultValue);
      return decimalValue.toNumber();
    } catch (error) {
      console.error('数值转换错误:', error);
      return Number(defaultValue) || 0;
    }
  }

  /**
   * 格式化结果
   * @private
   * @param {Decimal} result - Decimal.js 计算结果
   * @param {number} decimals - 保留小数位数
   * @returns {string}
   */
  static _formatResult(result, decimals) {
    if (decimals < 0) decimals = 0;

    // 使用 toFixed 并去除末尾的0
    const fixedResult = result.toFixed(decimals, Decimal.ROUND_HALF_UP);

    // 如果小数位数为0，直接返回整数部分
    if (decimals === 0) {
      return fixedResult.split('.')[0];
    }

    // 去除末尾的0和小数点（如果后面全是0）
    return fixedResult.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.$/, '');
  }

  /**
   * 链式操作支持（增强版，支持默认值）
   * @example
   * PrecisionUtils.chain(0.1).add(0.2).mul(10).value() // "3"
   */
  static chain(value, defaultValue = 0) {
    return new DecimalChain(value, defaultValue);
  }

  static mulThree(a, b, c, defaultValue = 0) {
      const decimalA = this._safeDecimal(a, defaultValue);
      const decimalB = this._safeDecimal(b, defaultValue);
      const decimalC = this._safeDecimal(c, defaultValue);
    const result = decimalA.times(decimalB).times(decimalC);
    return this._formatResult(result, defaultValue = 0);
  }
}

/**
 * 链式操作类（增强版）
 */
class DecimalChain {
  constructor(value, defaultValue = 0) {
    this._defaultValue = defaultValue;
    this._value = PrecisionUtils._safeDecimal(value, defaultValue);
  }

  add(value, defaultValue) {
    const decimalValue = PrecisionUtils._safeDecimal(
      value,
      defaultValue || this._defaultValue
    );
    this._value = this._value.plus(decimalValue);
    return this;
  }

  sub(value, defaultValue) {
    const decimalValue = PrecisionUtils._safeDecimal(
      value,
      defaultValue || this._defaultValue
    );
    this._value = this._value.minus(decimalValue);
    return this;
  }

  mul(value, defaultValue) {
    const decimalValue = PrecisionUtils._safeDecimal(
      value,
      defaultValue || this._defaultValue
    );
    this._value = this._value.times(decimalValue);
    return this;
  }

  div(value, defaultValue) {
    const decimalValue = PrecisionUtils._safeDecimal(
      value,
      defaultValue || this._defaultValue
    );

    if (decimalValue.isZero()) {
      console.warn('Division by zero in chain operation');
      this._value = new Decimal(this._defaultValue);
      return this;
    }

    this._value = this._value.dividedBy(decimalValue);
    return this;
  }

  value(decimals = 2) {
    return PrecisionUtils._formatResult(this._value, decimals);
  }

  toNumber() {
    return this._value.toNumber();
  }

  isValid() {
    return this._value.isFinite() && !this._value.isNaN();
  }

  mulThree(a, b, c, defaultValue) {
    const decimalValue = PrecisionUtils._safeDecimal(
      a,
      b,
      c,
      defaultValue || this._defaultValue
    );
    this._value = this._value.times(decimalValue);
    return this;
  }
}

// 导出为 Vue 插件
export default {
  install(Vue) {
    /**
     * @type {{
     *  add: typeof PrecisionUtils.add,
     *  sub: typeof PrecisionUtils.sub,
     *  mul: typeof PrecisionUtils.mul,
     *  div: typeof PrecisionUtils.div,
     *  mulThree: typeof PrecisionUtils.mulThree,
     *  fenToYuan: typeof PrecisionUtils.fenToYuan,
     *  yuanToFen: typeof PrecisionUtils.yuanToFen,
     *  compare: typeof PrecisionUtils.compare,
     *  chain: typeof PrecisionUtils.chain,
     *  isValidNumber: typeof PrecisionUtils.isValidNumber,
     *  toNumber: typeof PrecisionUtils.toNumber
     * }}
     */
    Vue.prototype.$money = PrecisionUtils;
  }
};
